import { useQuery } from "@tanstack/react-query";
import { useProjectContext } from "../context/ProjectContext";
import { type GetLogsParams, logsService } from "../services/logs";

export const logsQueryKeys = {
	base: ["logs"] as const,
	list: (projectId?: string, params?: GetLogsParams) =>
		[...logsQueryKeys.base, "list", projectId, params] as const,
	detail: (projectId?: string, requestId?: string) =>
		[...logsQueryKeys.base, "detail", projectId, requestId] as const,
};

/**
 * Hook to fetch paginated request logs
 */
export const useLogs = (
	params: GetLogsParams = {},
	options?: { autoRefresh?: boolean; refreshInterval?: number },
) => {
	const { selectedProject } = useProjectContext();
	const projectId = selectedProject?.id;
	const { autoRefresh = false, refreshInterval = 30000 } = options || {};

	return useQuery({
		queryKey: logsQueryKeys.list(projectId, params),
		queryFn: () => logsService.getLogs(projectId!, params),
		enabled: Boolean(projectId),
		staleTime: 1000 * 30, // 30 seconds
		refetchInterval: autoRefresh ? refreshInterval : false,
	});
};

/**
 * Hook to fetch a single log by request ID
 */
export const useLog = (requestId: string) => {
	const { selectedProject } = useProjectContext();
	const projectId = selectedProject?.id;

	return useQuery({
		queryKey: logsQueryKeys.detail(projectId, requestId),
		queryFn: () => logsService.getLogById(projectId!, requestId),
		enabled: Boolean(projectId && requestId),
	});
};
